import React, {useEffect} from 'react';
import Immutable from 'immutable';
import {connect, useSelector, useDispatch} from 'react-redux'
import {Route, Switch, useHistory} from "react-router-dom";
import {withRouter} from "react-router";
import LandingPage from './LandingPage';
import LoginPage from './LoginPage';
import LogoutPage from './LogoutPage';
import RegisterPage from './RegisterPage';
import EmailConfirmationPage from './EmailConfirmationPage';
import InvitationPage from './InvitationPage';
import ResetPasswordPage from './ResetPasswordPage';
import ProfilePage from './ProfilePage';
import ProfileEditPage from './ProfileEditPage';
import ProfileSearchPage from './ProfileSearchPage';
import ProfileCompletionPage from './ProfileCompletionPage';
import SettingsPage from './SettingsPage';
import GroupsPage from './GroupsPage';
import CustomPage from './CustomPage';
import Page from './Page';
import SidebarBanner from './SidebarBanner';
import ChatPage, {NewChatPage} from './ChatPage';
import SubscriptionPage from './SubscriptionPage';
import { WiderrufIntro, WiderrufForm } from './WiderrufPage';
import Header from './Header';
import AccountSubscriptions from './AccountSubscriptions';
import Footer from './Footer';
import LoadingScreen from './LoadingScreen';
import AdminApp from './admin';
import ModApp from './mods';
import AccountDeletedPage from './AccountDeletedPage';
import {Toast, ModalDialog} from './ui';

import {
   Segment, Container, Ref, Sidebar,
} from "semantic-ui-react";

import {
    clearToast, clearHeadModal, redirectTo, fetchInitialData,
} from "../actions";

import {
    getRedirectUrl, getToastMessages, getModalMessages, appLoaded,
} from "../selectors";

import {isPresent} from "../helpers";

const Redirector = () => {
    const redirectUrl = useSelector(getRedirectUrl);
    const history = useHistory();
    const dispatch = useDispatch();
    useEffect(() => {
        if (isPresent(redirectUrl)) {
            history.push(redirectUrl);
            dispatch(redirectTo(""));
        }
    }, [redirectUrl]);
    return null;
};

const Messages = () => {
    const toasts = useSelector(getToastMessages);
    const modals = useSelector(getModalMessages);
    const dispatch = useDispatch();
    return (
        <>
        <Toast.Container>
            {toasts.map((e, i) =>
                <Toast title={e.get('title')} key={e.get('key')}
                    onClose={() => dispatch(clearToast(e.get('key')))}
                >
                    {e.get('body')}
                </Toast>
            )}
        </Toast.Container>
        {modals.map((e, i) =>
            <ModalDialog title={e.get('title')} key={e.get('key')}
                onClose={() => dispatch(clearHeadModal())}
                actions={e.get('actions', Immutable.List())}
            >
                {e.get('body')}
            </ModalDialog>
        )}
        </>
    );
};

const AppRoutes = () => {
    const isLoaded = useSelector(appLoaded);
    if (!isLoaded) return null;
    return (
        <Switch>
            {/* TODO: check auth and mount only necessary routes */}
            <Route exact path="/" component={LandingPage} />
            <Route exact path="/login" component={LoginPage} />
            <Route exact path="/logout" component={LogoutPage} />
            <Route exact path="/register" component={RegisterPage} />
            <Route exact path="/byebye" component={AccountDeletedPage} />
            <Route exact path="/profile/edit" component={ProfileEditPage} />
            <Route exact path="/profile/init" component={ProfileCompletionPage} />
            {/* TODO: disallow slugs as edit, create, delete, update, index */}
            <Route exact path="/profile/:slug?" component={ProfilePage} />
            <Route exact path="/chat/:slug?" component={ChatPage} />
            <Route exact path="/chat-to/:slug?" component={NewChatPage} />
            <Route exact path="/subscribe/:status?" component={SubscriptionPage} />
            <Route exact path="/widerruf/form" component={WiderrufForm} />
            <Route exact path="/widerruf" component={WiderrufIntro} />
            <Route exact path="/subscriptions" component={AccountSubscriptions} />
            <Route exact path="/p/:slug" component={CustomPage} />
            <Route exact path="/groups/:slug?" component={GroupsPage} />
            <Route exact path="/confirmation" component={EmailConfirmationPage} />
            <Route exact path="/invitation" component={InvitationPage} />
            <Route exact path="/password-reset" component={ResetPasswordPage} />
            <Route path="/settings" component={SettingsPage} />
            <Route path="/search/" component={ProfileSearchPage} />
            <Route path="/admin/" component={AdminApp} />
            <Route path="/mods/" component={ModApp} />
            {/* TODO: Need a page not found route here */}
        </Switch>
    );
};

// The browser tab title is what a screen reader announces first on a new page,
// and it was "Kuscheldate" everywhere. First matching prefix wins.
const PAGE_TITLES = [
    ["/login", "Anmelden"],
    ["/register", "Profil anlegen"],
    ["/password-reset", "Passwort zurücksetzen"],
    ["/confirmation", "E-Mail bestätigen"],
    ["/invitation", "Einladung"],
    ["/profile/edit", "Profil bearbeiten"],
    ["/profile/init", "Profil vervollständigen"],
    ["/profile", "Profil"],
    ["/chat", "Nachrichten"],
    ["/search", "Suche"],
    ["/groups", "Kuschelgruppen"],
    ["/settings", "Einstellungen"],
    ["/subscriptions", "Mein Abonnement"],
    ["/subscribe", "Abonnement"],
    ["/widerruf", "Widerruf"],
    ["/admin", "Administration"],
    ["/mods", "Moderation"],
];

const pageTitle = (pathname) => {
    const match = PAGE_TITLES.find(([prefix]) => pathname.startsWith(prefix));
    return match ? `${match[1]} – Kuscheldate` : "Kuscheldate";
};

const firstSegment = (pathname) => pathname.split("/")[1];

class App extends React.PureComponent {
    constructor(props) {
        super(props);
        this.contextRef = React.createRef();
        this.state = {
            sidebarVisible: false,
        };
        this.onPusherClick = this.onPusherClick.bind(this);
        this.onSidebarToggle = this.onSidebarToggle.bind(this);
    }

    componentDidMount() {
        const {
            fetchInitialData,
        } = this.props;
        fetchInitialData();
        document.title = pageTitle(this.props.location.pathname);
    }

    componentDidUpdate(prevProps) {
        const {pathname} = this.props.location;
        if(pathname !== prevProps.location.pathname) {
            this.onPusherClick();
            document.title = pageTitle(pathname);
            // Moving to another page left focus on the link that was used, so
            // a screen reader did not notice the page had changed. Only when
            // the section changes: switching chats or settings tabs keeps the
            // member where they are. A field the new page focused itself
            // (autoFocus) is left alone.
            const main = document.getElementById("main-content");
            if (main && firstSegment(pathname) !== firstSegment(prevProps.location.pathname) &&
                !main.contains(document.activeElement)) {
                main.focus({preventScroll: true});
            }
        }
    }

    skipToContent(ev) {
        ev.preventDefault();
        document.getElementById("main-content")?.focus();
    }

    onPusherClick() {
        const {sidebarVisible} = this.state;
        if(sidebarVisible){
            this.setState({
                sidebarVisible: false,
            });
        }
    }

    onSidebarToggle(e) {
        this.setState((s, p) => ({
            sidebarVisible: !s.sidebarVisible,
        }));
        e.preventDefault();
    }

    render() {
        const { location } = this.props;
        const hideBanners = location.pathname.startsWith('/admin') || location.pathname.startsWith('/mods');
        // Two limits squeeze admin screens into roughly 825px: the Container below
        // caps the page at about 1127px, and main.scss reserves 680px for the side
        // banners - banners that hideBanners above has already switched off, so
        // that space sits empty. Both are lifted here.
        //
        // Applied to the whole admin area rather than just the Widerruf queue:
        // widening a single tab made the panel jump sideways whenever you switched
        // tabs, which read as a glitch. /mods is deliberately left out for now.
        const fullWidthPage = location.pathname.startsWith('/admin');

        return (
            <Ref innerRef={this.contextRef}>
            <div id="app">
                <a href="#main-content" className="skip-link" onClick={this.skipToContent}>
                    Zum Inhalt springen
                </a>
                <Sidebar.Pushable className={this.state.sidebarVisible?"open":"closed"}>
                    <Header.Sidebar visible={this.state.sidebarVisible}/>
                    <Sidebar.Pusher
                        dimmed={this.state.sidebarVisible}
                        onClick={this.onPusherClick}
                        style={{minHeight: "100vh"}}
                    >
                        <div className="bgPane">
                            <Header stickyRef={this.contextRef} onSidebarToggle={this.onSidebarToggle} />
                            <div id="page-content">
                                <div className={fullWidthPage ? "page-wrapper full-width-page" : "page-wrapper"}>
                                    {!hideBanners && (
                                        <div className="cd-sidebar-banner left">
                                            <SidebarBanner slug="left-banner" />
                                        </div>
                                    )}
                                    {/* role, not <main>: main.scss lays this out as div.segment */}
                                    <Segment role="main" id="main-content" tabIndex={-1}>
                                        <Container fluid={fullWidthPage}>
                                            <AppRoutes />
                                        </Container>
                                    </Segment>
                                    {!hideBanners && (
                                        <div className="cd-sidebar-banner right">
                                            <SidebarBanner slug="right-banner" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Footer />
                        <Redirector />
                        <Messages />
                        <LoadingScreen />
                    </Sidebar.Pusher>
                </Sidebar.Pushable>
            </div>
            </Ref>
        );
    }
}

App.defaultProps = {};
App.propTypes = {
};

const mapStateToProps = (state) => {
    return {
    };
};

const mapDispatchToProps = {
    fetchInitialData,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(App));
